import {
  ButtonHTMLAttributes,
  ComponentProps,
  ForwardedRef,
  ReactNode,
  RefAttributes,
  forwardRef,
} from 'react';
import { To } from 'react-router-dom';

import { Link } from '@sorare/routing';

import { useClickHandler } from 'hooks/useClickHandler';

type NativeButtonProps = ButtonHTMLAttributes<
  HTMLButtonElement | HTMLAnchorElement
> & {
  disableDebounce?: boolean;
};

type LinkButtonProps = {
  externalLink?: boolean;
} & Omit<ComponentProps<typeof Link>, 'to'>;

type AllButtonProps = Omit<NativeButtonProps, 'type'> &
  Omit<LinkButtonProps, 'ref'> &
  RefAttributes<HTMLButtonElement | HTMLAnchorElement>;

const NativeButtonBase = forwardRef<HTMLButtonElement, NativeButtonProps>(
  (props, ref) => {
    const { onClick, disableDebounce = true, type = 'button', ...rest } = props;
    const clickHandler = useClickHandler(onClick, disableDebounce);
    return (
      <button
        ref={ref as ForwardedRef<HTMLButtonElement>}
        onClick={clickHandler}
        /* eslint-disable react/button-has-type */
        type={type}
        {...rest}
      />
    );
  }
);
NativeButtonBase.displayName = 'NativeButtonBase';

// @ts-expect-error forwardRef is not working correctly with overloaded function
export const ButtonBase: {
  (args: { to: To } & LinkButtonProps): ReactNode;
  (args: { to: never } & NativeButtonProps): ReactNode;
  (args: { to?: To } & AllButtonProps): ReactNode;
} = forwardRef(function ButtonWithRef(
  props,
  ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>
) {
  if ('to' in props && props.to) {
    const { externalLink, ...rest } = props;
    return (
      <Link
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        {...rest}
        {...(externalLink
          ? ({ target: '_blank', rel: 'noopener noreferrer' } as any)
          : {})}
      />
    );
  }
  return (
    <NativeButtonBase {...props} ref={ref as ForwardedRef<HTMLButtonElement>} />
  );
});
